import { useContext, useEffect, useId, useState } from 'react';
import classNames from 'classnames';

import { PROCESSING_PLACEHOLDER } from '../../../shared/MediaProcessingItem';
import { DraggablePhotos, Modal } from '../../../shared';

import { useMediaProcessingStatus } from '../../../hooks/medias';

import { assert } from '../../../utils/assert';
import {
    OnCompleteCallback,
    OnPhotosSelectedCallback,
    onSelectPhoto,
    onSelectVideo,
    OnVideoUploadComplete,
    VideoFileUploading,
} from '../../../utils/upload';

import { MediaType } from '../../../types/enums/mediatype.enum';

import styles from './FileInput.module.scss';
import { MediaProcessingReporterContext } from './mediaProcessingReporter';

import dogImg from '../../../assets/dog.png';

export type FileType = 'video' | 'image';
const DEFAULT_FILE_TYPES: Array<FileType> = ['video', 'image'];

const mimeTypeToFileType = (mimeType: string): FileType => {
    const fileType = mimeType.slice(0, mimeType.indexOf('/'));

    return fileType as FileType;
};

export interface FileInputProps {
    onChange: (file?: { url: string; fileType: FileType }) => void;
    allowedFileTypes?: FileType | Array<FileType>;
    value: { url: string; fileType: FileType } | undefined;
}

export function FileInput({
    onChange,
    allowedFileTypes = DEFAULT_FILE_TYPES,
    value,
}: FileInputProps) {
    const id = useId();
    allowedFileTypes = Array.isArray(allowedFileTypes)
        ? allowedFileTypes
        : [allowedFileTypes];

    const [isUploading, setIsUploading] = useState(false);
    const {
        processingStatus,
        trackMediaProcessing,
        media: trackedMedia,
    } = useMediaProcessingStatus({
        onProcessingComplete: (media) => {
            if (!isTrackedMediaSelected) {
                return;
            }
            onChange?.({
                url: media.url,
                fileType: media.type === 'video' ? 'video' : 'image',
            });
        },
        onProcessingError: () => {
            if (!isTrackedMediaSelected) {
                return;
            }
            Modal.alert({
                heading: 'Processing Failed',
                text: 'The server failed to process the given file',
            });
            onChange(undefined);
        },
    });

    // The value may since have been removed or replaced, e.g. when the input is reused for another page.
    const isTrackedMediaSelected =
        trackedMedia !== undefined && value?.url === trackedMedia.url;
    const isProcessing =
        isTrackedMediaSelected &&
        processingStatus?.processingState === 'in_progress';

    const reportProcessing = useContext(MediaProcessingReporterContext);
    useEffect(() => {
        reportProcessing?.(id, isProcessing);
    }, [reportProcessing, id, isProcessing]);
    useEffect(
        () => () => reportProcessing?.(id, false),
        [reportProcessing, id],
    );

    const onPhotoSelected: OnPhotosSelectedCallback = (_type, fileList) => {
        assert(fileList.length === 1);
        const url = fileList[0].url;
        onChange({ url, fileType: 'image' });
        setIsUploading(true);
    };
    const onPhotoUpload: OnCompleteCallback = (_type, _file, res) => {
        onChange({ url: res.data.uploaded.url, fileType: 'image' });
        setIsUploading(false);
        trackMediaProcessing(res.data.uploaded);
    };

    function onVideoSelected(fileUploading: VideoFileUploading) {
        onChange({ url: fileUploading.url, fileType: 'video' });
        setIsUploading(true);
    }
    const onVideoUpload: OnVideoUploadComplete = (_file, res) => {
        assert(res.data.uploaded.length === 1);
        onChange({ url: res.data.uploaded[0].url, fileType: 'video' });
        setIsUploading(false);
        trackMediaProcessing(res.data.uploaded[0]);
    };

    const isDisabled = isUploading || Boolean(value) || isProcessing;
    return (
        <>
            {!value && (
                <div className="noMediaWrapper">
                    <img alt="placeholder no uploaded video" src={dogImg} />
                    <label className="placeholder">
                        {`No ${allowedFileTypes.join(
                            ' or ',
                        )}? Ohno. Click below to upload :)`}
                    </label>
                </div>
            )}
            {value && (
                <div className={styles.transparentBackground}>
                    <DraggablePhotos
                        type={
                            value.fileType === 'image'
                                ? MediaType.PORTAL
                                : MediaType.VIDEO
                        }
                        items={[
                            {
                                id: value.url,
                                // The CDN url 404s until the image is processed.
                                url:
                                    isProcessing && value.fileType === 'image'
                                        ? PROCESSING_PLACEHOLDER
                                        : value.url,
                                uploading: isUploading || isProcessing,
                                uploadingText: isProcessing
                                    ? `Processing: ${processingStatus.processingProgress}%`
                                    : 'Uploading',
                            },
                        ]}
                        onDelete={() => {
                            onChange(undefined);
                        }}
                    />
                </div>
            )}
            <div className={styles.fileInput}>
                <input
                    type="file"
                    name={id}
                    id={id}
                    accept={allowedFileTypes
                        .map((fileType) => `${fileType}/*`)
                        .join(',')}
                    disabled={isDisabled}
                    multiple={false}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            const fileType = mimeTypeToFileType(file.type);

                            switch (fileType) {
                                case 'image':
                                    onSelectPhoto(
                                        e,
                                        MediaType.PORTAL,
                                        onPhotoSelected,
                                        onPhotoUpload,
                                    );
                                    break;
                                case 'video':
                                    onSelectVideo(
                                        e,
                                        onVideoSelected,
                                        onVideoUpload,
                                        {
                                            compress: true,
                                        },
                                    );
                            }
                        }
                    }}
                />
                <label
                    className={classNames('upload', isDisabled && 'disabled')}
                    htmlFor={id}
                >
                    Add new
                </label>
            </div>
        </>
    );
}
